import { fetchWithAuth } from './api';

const host = window.location.hostname === 'localhost' ? "http://localhost:8000/api" : "/api";

interface OpportunityResponse {
  items: Array<{
    opportunity_finding_id: number;
    scenario_name: string;
    opportunity_ref: string;
    status: string;
    created_at: string;
    value?: string;
  }>;
  total: number;
  page: number;
  limit: number;
}

interface OpportunitySummary {
  open_opportunities: number;
  closed_opportunities: number;
  open_value: number;
  recovered_value: number;
}

interface Metadata {
  model: string;
  run_id: string;
  version: string;
  reasoning: string;
  source_nodes: Array<{
    text: string;
    score: number;
    file_id: number;
    node_id: string;
    file_name: string;
    file_type: string;
    page_label: number;
  }>;
  answer_context: {
    text: string;
    file_id: number;
    file_name: string;
    file_type: string;
    page_label: number;
  };
  retriever_class: string;
  additional_context: string;
  contract_workspace: string;
  retriever_settings: {
    top_k: number;
    reranking: boolean;
    extend_text: boolean;
    save_to_table: boolean;
    reranked_nodes: number;
    extend_by_nodes: number;
    provide_reasoning: boolean;
    pass_leaf_nodes_only: boolean;
  };
}

export const getOpportunities = async (page: number = 1, limit: number = 10, tabValue: number = 0, contractIds?: number[]) => {
  let endpoint;
  switch (tabValue) {
    case 0:
      endpoint = '/opportunities/open';
      break;
    case 1:
      endpoint = '/opportunities/closed';
      break;
    case 2:
      endpoint = '/opportunities/not-found';
      break;
    default:
      endpoint = '/opportunities/open';
  }
  
  let url = host + `${endpoint}?page=${page}&limit=${limit}`;
  
  // Add contract_ids parameter if provided
  if (contractIds && contractIds.length > 0) {
    url += `&contract_ids=${contractIds.join(',')}`;
  }
  
  const response = await fetchWithAuth(
    url,
    {
      method: 'GET'
    }
  );
  const data = await response.json() as OpportunityResponse;
  return {
    ...data,
    items: data.items.map(item => ({
      id: item.opportunity_finding_id,
      opportunity_name: item.scenario_name,
      client_name: item.opportunity_ref,
      status: item.status,
      value: item.value || '-',
      created_date: new Date(item.created_at).toLocaleDateString(),
    }))
  };
};

export const getOpportunitiesSummary = async () => {
  const response = await fetchWithAuth(
    host + '/opportunities/summary',
    {
      method: 'GET'
    }
  );
  const data = await response.json() as OpportunitySummary;
  return {
    openOpportunities: data.open_opportunities,
    closedOpportunities: data.closed_opportunities,
    totalValueOpenOpps: new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(data.open_value),
    totalValueRecovered: new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(data.recovered_value)
  };
};

export const getOpportunity = async (id: number) => {
  console.log('Fetching opportunity:', id);
  const response = await fetchWithAuth(
    host + `/opportunities/${id}`,
    {
      method: 'GET'
    }
  );
  const item = await response.json();
  console.log('Raw API response:', item);

  // metadata_ is already an object, no need to parse
  const metadata: Metadata | null = item.metadata_ || null;
  console.log('Metadata:', metadata);

  // const references = metadata?.source_nodes?.map((node, index) => ({
  //   id: index + 1,
  //   fileName: node.file_name,
  //   pageNumber: node.page_label + 1,
  //   fileId: node.file_id.toString(),
  //   text: node.text
  // })) || [];
  interface ReferenceNode {
    fileName: string;
    pageNumber: number;
    fileId: string;
    text: string;
  }

  const references = item?.references?.map((node: ReferenceNode, index: number) => ({
    id: index + 1,
    fileName: node.fileName,
    pageNumber: node.pageNumber + 1,
    fileId: node.fileId.toString(),
    text: node.text
  })) || [];
  console.log('Mapped references:', references);

  const result = {
    id: item.opportunity_finding_id,
    opportunity_name: item.scenario_name,
    client_name: item.opportunity_ref,
    status: item.status,
    value: item.value || '-',
    created_date: new Date(item.created_at).toLocaleDateString(),
    reasoning: metadata?.reasoning || '',
    answer: item.answer || '',
    references,
    email_body: item.email_body || '',
    mailto_url: item.mailto_url || '',
    metadata_: item.metadata_ || {},
    scenario: item.scenario || null
  };
  console.log('Transformed opportunity data:', result);
  return result;
};

export const updateOpportunityStatus = async (opportunityId: number, status: string) => {
  return await fetchWithAuth(
    host + `/opportunities/${opportunityId}/status`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status })
    }
  );
};

export const generateEmail = async (opportunityDetails: any, onChunk: (chunk: string) => void) => {
  try {
    const response = await fetchWithAuth(
      host + `/opportunities/generate-email`,
      {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(opportunityDetails)
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('No response body received');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      onChunk(chunk);
    }
  } catch (error) {
    console.error('Error in generateEmail:', error);
    throw error;
  }
};

// Function to get contract workspaces for filtering
export const getContractWorkspaces = async (): Promise<Array<{id: string, label: string}>> => {
  const { getContractList } = await import('./api');
  const response = await getContractList('', 'name');
  
  if (response?.data?.contracts) {
    // Filter to only include ANALYSED contracts and format for dropdown
    return response.data.contracts
      .filter((contract: any) => contract.status === 'ANALYSED')
      .map((contract: any) => ({
        id: contract.contract_id.toString(),
        label: contract.contract_workspace || 'Unnamed Workspace'
      }));
  }
  return [];
};